import { Action, createReducer, on } from "@ngrx/store";

import * as PropertiesActionTypes from "../../actions/properties/properties.action";

import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { ColumnSorterInfo } from "src/app/models/custom-table/custom-table.model";

export interface PropertiesState {
  properties: any[];
  usages: any[];
  dataTypes: any[];
  created: boolean;
  property: any;
  isLoading: boolean;
  propertiesTableConfig: ColumnSorterInfo[],
  error: Error;
  totalRecords: number;
  pageReq: PaginationRequest;
  excelProperties: any[];
  totalProperties: number;
}

export const initialState: PropertiesState = {
  properties: null,
  usages: null,
  dataTypes: null,
  created: false,
  property: null,
  isLoading: false,
  propertiesTableConfig: null,
  error: null,
  totalRecords: 0,
  pageReq: null,
  excelProperties: null,
  totalProperties: null,
};

const propertiesReducer = createReducer(
  initialState,
  on(PropertiesActionTypes.resetPropertiesState, () => initialState),
  on(
    PropertiesActionTypes.getPropertiesList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as PropertiesState)
  ),
  on(PropertiesActionTypes.loadPropertiesList, (state, { response }) => {
    return {
      ...state,
      properties: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
      totalProperties: (response as any)?.result?.size
        ? (response as any)?.result?.size
        : 0,
    };
  }),
  on(
    PropertiesActionTypes.getExcelPropertiesList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as PropertiesState)
  ),
  on(PropertiesActionTypes.loadExcelPropertiesList, (state, { response }) => {
    return {
      ...state,
      excelProperties: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    PropertiesActionTypes.getPropertiesUsagesList,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as PropertiesState)
  ),
  on(PropertiesActionTypes.loadPropertiesUsagesList, (state, { response }) => {
    return {
      ...state,
      usages: (response as any).result.items
        ? (response as any).result.items
        : [],
      error: null,
    };
  }),
  on(
    PropertiesActionTypes.getPropertiesDataTypeList,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as PropertiesState)
  ),
  on(
    PropertiesActionTypes.loadPropertiesDataTypeList,
    (state, { response }) => {
      return {
        ...state,
        dataTypes: (response as any).result.items
          ? (response as any).result.items
          : [],
        error: null,
      };
    }
  ),
  on(
    PropertiesActionTypes.getPropertiesFilteredList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
      } as PropertiesState)
  ),
  on(
    PropertiesActionTypes.loadPropertiesFilteredList,
    (state, { response }) => {
      let currentOffset = state.pageReq ? state.pageReq.offset : 0;
      let totalRecords = Math.max(
        currentOffset + (response as any)?.result?.size,
        state?.totalRecords
      );
      return {
        ...state,
        properties: (response as any).result
          ? (response as any).result
          : undefined,
        error: null,
        totalRecords: totalRecords,
      };
    }
  ),
  on(PropertiesActionTypes.getPropertiesTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),
  on(PropertiesActionTypes.loadPropertiesTableConfig, (state, { response }) => {
    return {
      ...state,
      propertiesTableConfig: (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(PropertiesActionTypes.savePropertiesTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(PropertiesActionTypes.setProperty, (state, { property }) => ({
    ...state,
    property: (property as any).result,
    isLoading: false,
    error: null,
  })),
  on(PropertiesActionTypes.resetProperty, (state) => {
    return {
      ...state,
      property: null,
      error: null,
    };
  }),
  on(PropertiesActionTypes.setLoadingEndState, (state) => {
    return {
      ...state,
      isLoading: false,
    };
  }),
  on(PropertiesActionTypes.getProperty, (state) => {
    return {
      ...state,
      isLoading: true,
      created: false,
    };
  }),
  on(PropertiesActionTypes.resetCreation, (state) => {
    return {
      ...state,
      created: true,
    };
  }),
  on(
    PropertiesActionTypes.createProperty,
    PropertiesActionTypes.updateProperty,
    (state) => {
      return {
        ...state,
        isLoading: true,
      };
    }
  )
);

export function reducer(state: PropertiesState | undefined, action: Action) {
  return propertiesReducer(state, action);
}
